import { type RouteConfig } from "aurelia-router";
import { ItemSource } from "./queries.models";
import { ClaimsRequireTypeEnum } from "./user.models";

export enum EntityTypes {
    None = 0,
    Customer = 1,
    Vessel = 2,
    Company = 3,
    Vet = 4,
    PSC = 5,
    TVQ = 6,
    VetTask = 8,
    SubtaskCorrespondence = 9,
    List = 10,
    ListItem = 11,
    Incident = 12,
    RightShipInspection = 13,
    FBR = 16,
    FBRItem = 17,
    GHGVerification = 18,
    Cfr = 19,
    CAPSurvey = 20,
    ThirdPartyInspection = 21,
    IncidentOverride = 23,
    CustomReport = 24,
    RuleConfig = 25,
    RightshipRuleConfigParameter = 26,
    BusinessUnitRuleConfigParameter = 27,
    CustomerRuleConfigParameter = 28,
    VesselApplicationForms = 32,
    SafetyRatingReview = 33,
    SafetyRatingReviewTask = 34,
    WorkflowAssignment = 35,
    VoyageDataSubmission = 36,
    CarbonAccountingEnrollment = 38,
    LegacyCrewWelfare = 39,
    BusinessUnit = 44,
    RuleCollection = 45,
    ZeroharmPartner = 47,
    CrewWelfare = 48,
    CrewWelfareChapter = 49,
    CrewWelfareCriteria = 50,
    CompanyOrFreelancedInspector = 55,
    DocumentRequirementSubsciptionVessels = 56,
    VesselDocument = 57,
    VesselForm = 58,
    DocumentAccessRequest = 59,

    //Genesis Types
    User = 1000,
    Branch = 1001,
    Department = 1002,
    Role = 1003,
    Permission = 1004,
    BranchPermission = 1005,
    DepartmentPermission = 1006,
    RolePermission = 1007,
    UserPermission = 1008,
    Dashboard = 1009,
    UserDashboard = 1010,
    DashboardWidget = 1011,
    ActivityLog = 1012,
    EventLog = 1013,
    OnboardingRequest = 1014,
    OnboardingConfig = 1015,
    WorkflowConfiguration = 1016,
    OnboardingConfigVerification = 1017,
    Offering = 1018,
    OnboardingConfigLandingPage = 1019,
    OfferingSelectionConfig = 1020,
    GdprProcess = 1021,
    GdprPolicy = 1022,
    GdprPersonalDataRecord = 1023,
    GdprDataSubject = 1024,
    GdprCase = 1025,
    Attachments = 1026,
    RoleUser = 1028,
    UserPhoneNumber = 1029,
    ExcelImportLog = 1030,
    MonitoringProcess = 1031,
    MonitoringProcessResult = 1032,
    Lookup = 1033,
    GenesisExample = 9999,
    Moc = 331732,
    ClassSurvey = 766742
}

export enum RightShipInspectionType {
    RightShipInspection = 1,
    CargillSurvey = 2,
    TankerInspection = 3
}

export enum ResponseType {
    DOMString = 'text',
    ArrayBuffer = 'arraybuffer',
    Blob = 'blob',
    Document = 'document',
    JObject = 'json',
}

export interface IAssignEntity extends IEntityTypeIdentifier {
    assignedTo?: number;
    assignedDate?: Date,
    assignmentComment?: string;
    assignedToUserName?: string;
    id?: number
}

export interface IEntityTypeIdentifier extends IBaseEntity {
    entityType?: EntityTypes;
}

export interface IBaseEntity {
    identifier: string;
}

export enum KeyCode {
    Enter = 13,
    ArrowUp = 38,
    ArrowDown = 40
}

export interface BaseResponse {
    id: number;
}

export interface NavigationSection {
    title: string;
    icon: string;
    isOpen: boolean;
    isVisible: boolean;
    isSectionHeadVisible: boolean;
    menuTabs: MenuTab[];
}

export interface MenuTab {
    key: string;
    title?: string | (() => string);
    icon?: string | (() => string) | SvgIcon;
    routes: ExtendedRouteConfig[];
    isVisible: boolean;
    showDropdownMenu?: boolean; //for touch device clicks
    lockedFeature?: boolean;
}

export interface SvgIcon {
    class: string;
    d?: string;
    viewBox?: string;
    width?: string;
    height?: string;
}

export interface TabModel {
    /* Tab index */
    step?: any;
    /* Tab title */
    title?: string | (() => string);
    /* View-model to-be bound to the view */
    vm?: any;
    children?: TabModel[];
    parent?: any;
    /* Show the tab in the navigation bar */
    show: boolean | (() => boolean);
    isHiddenFromStepper?: boolean;
    onClick?: ((x: any) => void | boolean | Promise<void>);
    getModel?: (item) => any;
    errorsCount?: number;
    class?: string;
    lazyLoad?: boolean;
    isOpen?: boolean;
    lockedFeature?: boolean;
    unlockFeature?: () => void;
    param?: string;
}

export interface ExtendedRouteConfig extends RouteConfig {
    queryString?: string;
    entity?: string;
    class?: string;
    iconType?: number;
    icon?: string | (() => string) | SvgIcon;
    claimsRequired?: string[] | (() => string[]);
    entityTypeRoute?: string;
    children?: ExtendedRouteConfig[];
    isVisible?: boolean;
    pageCount?: number;
    onItemSourcePrepared?: (itemSource: ItemSource) => void;
    // Explicit uri for the custom header component in queries.html. The default is uri.
    headerUri?: string;
    // Explicit uri for the custom nav bar component in queries.html. The default is uri.
    navbarUri?: string;
    // Specifies that you want the header component to be rendered.
    hasHeader?: boolean;
    // Override the default text that is in the add new button in queries-editor.html
    addNewTextOverride?: string;
    // default selection mode for the queries data-grid. Default is single.
    selectionMode?: (() => string) | string;
    claimsRequireType?: ClaimsRequireTypeEnum;
}

export enum OnboardingRequestStatusEnum {
    PendingFormInput = 1,
    PendingTerminalRSReview = 2,
    Accepted = 3,
    Unacceptable = 4,
    Cancelled = 5,
    RequestingInfo = 6,
    AcceptableDayOnly = 1000,
    Completed = 1001
}

export enum SubtaskSourceEnum {
    MessageCentre = 1
}